<script setup>
  import { computed, onBeforeUnmount, watch } from "vue";
  import { storeToRefs } from "pinia";
  import { useRouter } from "vue-router";
  import { useForm } from "vee-validate";
  import { toTypedSchema } from "@vee-validate/zod";
  import { z } from "zod";
  import { Info } from "lucide-vue-next";
  import { DateTimePicker } from "@/components/episto-ui/date-time-picker";
  import { Banner } from "@/components/episto-ui/banner";
  import OnePanePage from "@/templates/OnePanePage.vue";
  import UploadFileButton from "@/components/Survey/Edit/UploadFileButton.vue";
  import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
  import { FormItem, FormField, FormControl, FormLabel, FormMessage } from "@/components/ui/form";
  import { Input } from "@/components/ui/input";
  import { Label } from "@/components/ui/label";
  import { Button } from "@/components/ui/button";
  import { Checkbox } from "@/components/ui/checkbox";
  import { Tooltip } from "@/components/ui/tooltip";

  import { useMailingListStore } from "@stores/survey/mailing/mailingList";

  const {
    isNewList,
    mailingList,
    mailingListCsvFile,
    getCsvFileName,
    isListExpired,
    initialMailingList,
    hasListChanged,
  } = storeToRefs(useMailingListStore());
  const { createMailingList, updateMailingList, uploadContactsFile, resetListCsvFile } = useMailingListStore();
  const router = useRouter();

  const headerTitle = computed(() => (isNewList.value ? "New list" : `Edit ${mailingList.value?.name}`));

  const validationSchema = toTypedSchema(
    z
      .object({
        name: z
          .string({ required_error: "Please enter a name for this list" })
          .min(1, "Please enter a name for this list"),
        expired_at: z.string().optional(),
        shared: z.boolean().optional(),
      })
      .superRefine((data, ctx) => {
        if (!data.shared) {
          if (!data.expired_at) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: "Please select an expiration date",
              path: ["expired_at"],
            });
          } else if (new Date(data.expired_at) < new Date()) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: "The expiration date must be after the current date",
              path: ["expired_at"],
            });
          }
        }

        if (data.shared === false && !isNewList.value && initialMailingList?.value?.shared) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Once a list is shared, it cannot be unshared",
            path: ["shared"],
          });
        }
      }),
  );

  const { handleSubmit, meta, values } = useForm({
    validationSchema,
    validateOnMount: false,
    initialValues: { ...mailingList.value },
  });

  const hasErrors = computed(() => meta.value.dirty && !meta.value.valid);

  watch(values, (v) => Object.assign(mailingList.value, v));

  const onSubmit = handleSubmit(async () => {
    if (isNewList.value) await createMailingList();
    else await updateMailingList();

    router.push({ name: "SurveyMailingLists" });
  });

  const handleListFileUpload = async ({ file }) => {
    await uploadContactsFile(file);
  };

  onBeforeUnmount(() => {
    if (mailingListCsvFile.value) resetListCsvFile();
  });
</script>

<template>
  <OnePanePage>
    <Card
      data-cy="survey-mailing-lists-new-list-page survey-mailing-lists-edit-list-page"
      class="max-w-3xl mx-auto p-6">
      <CardHeader class="pb-6">
        <CardTitle class="heading-page">{{ headerTitle }}</CardTitle>
        <Banner
          v-if="isListExpired"
          variant="info"
          message="This list has expired and cannot be modified. Please create a new one" />
      </CardHeader>
      <CardContent class="space-y-4">
        <FormField
          v-slot="{ componentField }"
          name="name">
          <FormItem>
            <FormLabel>Name <span class="text-destructive">*</span></FormLabel>
            <FormControl>
              <Input
                v-bind="componentField"
                :disabled="isListExpired"
                maxlength="18" />
            </FormControl>
            <FormMessage />
          </FormItem>
        </FormField>

        <FormField
          v-if="!mailingList?.shared"
          v-slot="{ componentField }"
          name="expired_at">
          <FormItem>
            <FormLabel class="flex items-center gap-1.5">
              Expiration date
              <Tooltip
                content="The contacts on this list, along with their personal information, will be deleted once the expiration date is reached">
                <Info
                  :stroke-width="1.5"
                  class="size-3.5 text-muted-foreground" />
              </Tooltip>
            </FormLabel>
            <FormControl>
              <DateTimePicker
                v-bind="componentField"
                :disabled="isListExpired"
                :clearable="false" />
            </FormControl>
            <FormMessage />
          </FormItem>
        </FormField>

        <FormItem>
          <Label class="flex items-center gap-1.5">
            Import contacts from a CSV file
            <Tooltip content="Create or update contacts and their attributes">
              <Info
                :stroke-width="1.5"
                class="size-3.5 text-muted-foreground" />
            </Tooltip>
          </Label>
          <UploadFileButton
            type="text"
            :attachment="getCsvFileName"
            :handle-upload="handleListFileUpload" />
        </FormItem>

        <FormField
          v-slot="{ value, handleChange }"
          name="shared">
          <FormItem>
            <div class="flex items-center gap-2">
              <FormControl>
                <Checkbox
                  :model-value="value"
                  :disabled="initialMailingList?.shared"
                  @update:model-value="handleChange" />
              </FormControl>
              <FormLabel class="font-normal"
                >Account level list (make it available to all surveys in your account)</FormLabel
              >
            </div>
            <FormMessage />
          </FormItem>
        </FormField>

        <div class="pt-4">
          <Button
            type="button"
            :variant="hasErrors ? 'destructive' : 'default'"
            :disabled="!hasListChanged || isListExpired || hasErrors"
            @click="onSubmit">
            Save
          </Button>
        </div>
      </CardContent>
    </Card>
  </OnePanePage>
</template>
